/**
 * Convertit une instance générée par le moteur déterministe du Blueprint
 * (@jeraisonne/db/blueprint) en opérations d'insertion de blocs V2 —
 * statement + statement_question + correction[+distractor], prêtes pour
 * `exercise-blocks-mutate` (chemin d'écriture unique, cf. applyBlockOps).
 *
 * ⚠️ Depuis la migration 0229 (« UN exercice paramétré »), ces ops ne sont
 * écrites QU'UNE FOIS, pour matérialiser l'exercice à partir de l'instance
 * CANONIQUE. Les autres tirages ne créent plus d'exercice : ils deviennent des
 * lignes `blueprint_instances` dont le calque est appliqué à ces mêmes blocs
 * au moment de la tentative. Les `client_id` utilisés ici sont donc contractuels
 * — ils servent à reconstruire le `block_map` depuis le `client_id_map` renvoyé
 * par l'edge function (cf. blueprintBlockMapFromClientIdMap).
 *
 * Pur — aucun accès réseau.
 */

import {
  formatNumber,
  BLUEPRINT_STATEMENT_CLIENT_ID,
  blueprintQuestionClientId,
  blueprintCorrectionClientId,
  blueprintDistractorClientId,
  type GeneratedInstance,
} from '@jeraisonne/db/blueprint';
import type { BlockMutationOp } from '@/lib/exercise-v2/types';
import { toExerciseVisualBlock } from './generated-visual-to-block';

export function generatedInstanceToBlockOps(instance: GeneratedInstance): BlockMutationOp[] {
  const ops: BlockMutationOp[] = [];

  ops.push({
    op: 'insert',
    client_id: BLUEPRINT_STATEMENT_CLIENT_ID,
    block_type: 'statement',
    section: 'statement',
    parent_id: null,
    position: 1,
    data: {
      content_md: instance.statement_md,
      ...(instance.visual ? { visuals: [toExerciseVisualBlock(instance.visual)] } : {}),
    },
  });

  instance.questions.forEach((question, i) => {
    const questionClientId = blueprintQuestionClientId(i);
    ops.push({
      op: 'insert',
      client_id: questionClientId,
      block_type: 'statement_question',
      section: 'statement',
      parent_id: null,
      position: i + 2,
      data: { prompt_md: question.prompt_md },
    });

    const correctionClientId = blueprintCorrectionClientId(i);
    // `explanation_md` est le corrigé DE CETTE question (étapes du solve path
    // qu'elle déclare via `solveStepIds`), calculé par le moteur — plus le
    // dump intégral du corrigé dans chaque correction comme en 0221.
    if (question.answerMode === 'calculator') {
      const answerText = question.unitLabel
        ? `${formatNumber(question.expectedValue)} ${question.unitLabel}`
        : formatNumber(question.expectedValue);
      ops.push({
        op: 'insert',
        client_id: correctionClientId,
        block_type: 'correction',
        section: 'correction',
        parent_id: questionClientId,
        position: i * 10 + 1,
        data: {
          explanation_md: question.explanation_md,
          answer_md: answerText,
          answer_mode: 'calculator',
          expected_value: question.expectedValue,
          tolerance: question.tolerance,
          ...(question.unitLabel ? { unit_label: question.unitLabel } : {}),
          available_variables: question.availableVariables,
        },
      });
    } else {
      ops.push({
        op: 'insert',
        client_id: correctionClientId,
        block_type: 'correction',
        section: 'correction',
        parent_id: questionClientId,
        position: i * 10 + 1,
        data: { explanation_md: question.explanation_md, answer_md: question.correctText_md },
      });
      question.distractors_md.forEach((label_md, j) => {
        ops.push({
          op: 'insert',
          client_id: blueprintDistractorClientId(i, j),
          block_type: 'distractor',
          section: 'correction',
          parent_id: correctionClientId,
          position: i * 10 + 2 + j,
          data: { label_md },
        });
      });
    }
  });

  return ops;
}
